// Chemin: C:\gestion-copro\src\services\interlocuteur.service.ts
// Session 151 : Ajout types notaire, conseil_syndical, collectivite, coproprietaire
//               Remplacement labels hardcodés par import de TYPE_INTERLOCUTEUR_LABELS
// Session 174 : Ajout type locataire dans TYPES_INTERLOCUTEUR

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  UpdateData
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Interlocuteur,
  InterlocuteurCreateData,
  InterlocuteurUpdateData,
  InterlocuteurAvecContactPrincipal,
  TypeInterlocuteur,
  Contact,
  TYPE_INTERLOCUTEUR_LABELS
} from '../types/interlocuteur.types';

const COLLECTION_NAME = 'interlocuteurs';

/**
 * Récupère tous les interlocuteurs d'une copropriété
 */
export async function getInterlocuteurs(
  coproprieteId: string,
  actifSeulement: boolean = true
): Promise<InterlocuteurAvecContactPrincipal[]> {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      orderBy('raisonSociale', 'asc')
    );

    if (actifSeulement) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('coproprieteId', '==', coproprieteId),
        where('actif', '==', true),
        orderBy('raisonSociale', 'asc')
      );
    }

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      const interlocuteur: InterlocuteurAvecContactPrincipal = {
        id: doc.id,
        ...data,
        contactPrincipal: data.contacts?.find((c: Contact) => c.principal) || data.contacts?.[0]
      } as InterlocuteurAvecContactPrincipal;
      
      return interlocuteur;
    });
  } catch (error) {
    console.error('Erreur lors de la récupération des interlocuteurs:', error);
    throw error;
  }
}

/**
 * Récupère les interlocuteurs par type
 */
export async function getInterlocuteursByType(
  coproprieteId: string,
  type: TypeInterlocuteur
): Promise<InterlocuteurAvecContactPrincipal[]> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      where('type', '==', type),
      where('actif', '==', true),
      orderBy('raisonSociale', 'asc')
    );

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        contactPrincipal: data.contacts?.find((c: Contact) => c.principal) || data.contacts?.[0]
      } as InterlocuteurAvecContactPrincipal;
    });
  } catch (error) {
    console.error('Erreur lors de la récupération des interlocuteurs par type:', error);
    throw error;
  }
}

/**
 * Récupère un interlocuteur par son ID
 */
export async function getInterlocuteur(interlocuteurId: string): Promise<Interlocuteur | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, interlocuteurId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Interlocuteur;
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Crée un nouvel interlocuteur
 */
export async function createInterlocuteur(
  data: InterlocuteurCreateData
): Promise<Interlocuteur> {
  try {
    // Valider qu'il y a au moins un contact
    if (!data.contacts || data.contacts.length === 0) {
      throw new Error('Au moins un contact est requis');
    }

    // S'assurer qu'un seul contact est principal
    const contactsNormalises = normaliserContacts(data.contacts);

    const interlocuteurData: Omit<Interlocuteur, 'id'> = {
      coproprieteId: data.coproprieteId,
      type: data.type,
      raisonSociale: data.raisonSociale,
      siret: data.siret,
      adresse: data.adresse,
      contacts: contactsNormalises,
      contrat: data.contrat ? {
        dateDebut: data.contrat.dateDebut ? Timestamp.fromDate(data.contrat.dateDebut) : undefined,
        dateFin: data.contrat.dateFin ? Timestamp.fromDate(data.contrat.dateFin) : undefined,
        typeContrat: data.contrat.typeContrat,
        montantAnnuel: data.contrat.montantAnnuel
      } : undefined,
      actif: data.actif !== undefined ? data.actif : true,
      notes: data.notes,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp
    };

    // Nettoyer les champs undefined
    const cleanData = JSON.parse(JSON.stringify(interlocuteurData));

    const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanData);

    return {
      id: docRef.id,
      ...interlocuteurData
    } as Interlocuteur;
  } catch (error) {
    console.error('Erreur lors de la création de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Met à jour un interlocuteur existant
 */
export async function updateInterlocuteur(
  interlocuteurId: string,
  data: InterlocuteurUpdateData
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, interlocuteurId);
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: UpdateData<any> = {
      updatedAt: serverTimestamp()
    };

    // Champs simples
    if (data.type !== undefined) updateData.type = data.type;
    if (data.raisonSociale !== undefined) updateData.raisonSociale = data.raisonSociale;
    if (data.siret !== undefined) updateData.siret = data.siret;
    if (data.actif !== undefined) updateData.actif = data.actif;
    if (data.notes !== undefined) updateData.notes = data.notes;

    // Adresse complète (on remplace tout le bloc)
    if (data.adresse !== undefined) {
      updateData.adresse = data.adresse;
    }

    // Contacts (on remplace tout le tableau)
    if (data.contacts !== undefined) {
      updateData.contacts = normaliserContacts(data.contacts);
    }

    // Contrat (mise à jour partielle)
    if (data.contrat !== undefined) {
      if (data.contrat.dateDebut !== undefined) {
        updateData['contrat.dateDebut'] = data.contrat.dateDebut 
          ? Timestamp.fromDate(data.contrat.dateDebut as unknown as Date)
          : null;
      }
      if (data.contrat.dateFin !== undefined) {
        updateData['contrat.dateFin'] = data.contrat.dateFin
          ? Timestamp.fromDate(data.contrat.dateFin as unknown as Date)
          : null;
      }
      if (data.contrat.typeContrat !== undefined) {
        updateData['contrat.typeContrat'] = data.contrat.typeContrat;
      }
      if (data.contrat.montantAnnuel !== undefined) {
        updateData['contrat.montantAnnuel'] = data.contrat.montantAnnuel;
      }
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Désactive un interlocuteur (soft delete)
 */
export async function desactiverInterlocuteur(interlocuteurId: string): Promise<void> {
  try {
    await updateInterlocuteur(interlocuteurId, { actif: false });
  } catch (error) {
    console.error('Erreur lors de la désactivation de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Réactive un interlocuteur
 */
export async function reactiverInterlocuteur(interlocuteurId: string): Promise<void> {
  try {
    await updateInterlocuteur(interlocuteurId, { actif: true });
  } catch (error) {
    console.error('Erreur lors de la réactivation de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Supprime définitivement un interlocuteur
 * Attention: à utiliser avec précaution, préférer la désactivation
 */
export async function deleteInterlocuteur(interlocuteurId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, interlocuteurId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'interlocuteur:', error);
    throw error;
  }
}

/**
 * Normalise les contacts pour s'assurer qu'un seul est principal
 */
function normaliserContacts(contacts: Contact[]): Contact[] {
  if (contacts.length === 0) return [];

  // Vérifier s'il y a un contact principal
  const principalExiste = contacts.some(c => c.principal);

  if (!principalExiste) {
    // Marquer le premier comme principal
    return contacts.map((c, index) => ({
      ...c,
      principal: index === 0
    }));
  }

  // S'assurer qu'il n'y en a qu'un seul
  let principalTrouve = false;
  return contacts.map(c => {
    if (c.principal && !principalTrouve) {
      principalTrouve = true;
      return c;
    }
    return { ...c, principal: false };
  });
}

/**
 * Retourne le libellé français d'un type d'interlocuteur
 * Session 151 : utilise TYPE_INTERLOCUTEUR_LABELS importé depuis les types (source unique)
 */
export function getTypeInterlocuteurLabel(type: TypeInterlocuteur): string {
  return TYPE_INTERLOCUTEUR_LABELS[type] || type;
}

/**
 * Liste des types d'interlocuteurs pour les formulaires
 * Session 151 : ajout notaire, conseil_syndical, collectivite, coproprietaire
 * Session 174 : ajout locataire comme type à part entière
 */
export const TYPES_INTERLOCUTEUR: { value: TypeInterlocuteur; label: string }[] = [
  { value: 'syndic', label: 'Syndic' },
  { value: 'promoteur', label: 'Promoteur' },
  { value: 'constructeur', label: 'Constructeur' },
  { value: 'prestataire', label: 'Prestataire' },
  { value: 'assurance', label: 'Assurance' },
  { value: 'avocat', label: 'Avocat' },
  { value: 'notaire', label: 'Notaire' },
  { value: 'expert', label: 'Expert' },
  { value: 'conseil_syndical', label: 'Conseil syndical' },
  { value: 'collectivite', label: 'Collectivité' },
  { value: 'coproprietaire', label: 'Copropriétaire' },
  { value: 'locataire', label: 'Locataire' },
  { value: 'autre', label: 'Autre' },
];
